import { LitElement, html } from "lit";
import { customElement, state } from "lit/decorators.js";

type Product = {
  name: string;
  tag: string;
  price: number;
  summary: string;
  details: string[];
  image?: string | null;
  createdAt?: string;
  handle?: string;
};

type CatalogStatus = "loading" | "live" | "empty" | "error";

type ShopifyProduct = {
  id: number;
  title: string;
  handle: string;
  tag: string;
  price: number;
  summary: string;
  details: string[];
  image?: string | null;
  createdAt?: string;
};

type ShopifyCatalogResponse = {
  shop: {
    name: string;
    domain: string;
    myshopifyDomain: string;
    currency: string;
  };
  products: ShopifyProduct[];
};

type ViewName = "home" | "shop" | "collections" | "category" | "product" | "story" | "care";

type RouteState = {
  view: ViewName;
  categorySlug: string | null;
  productHandle: string | null;
};

type StoreCategory = {
  slug: string;
  label: string;
  description: string;
  productCount: number;
  featured: Product;
};

const assetBase = import.meta.env.BASE_URL;
const storyCoverAsset = `${assetBase}dalenor-cover-art-backup.svg`;

function formatPrice(value: number, currency: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(value);
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function excerpt(value: string, length = 120): string {
  const normalized = value.replace(/\s+/g, " ").trim();
  if (normalized.length <= length) return normalized;
  return `${normalized.slice(0, length - 1).trimEnd()}…`;
}

function normalizeProduct(product: ShopifyProduct): Product {
  const details = product.details.length > 0 ? product.details : [product.tag, "Published on Shopify", "Available now"];

  return {
    name: product.title,
    tag: product.tag,
    price: product.price,
    summary: product.summary,
    details,
    image: product.image ?? null,
    createdAt: product.createdAt,
    handle: product.handle,
  };
}

function categoryCopy(slug: string, label: string) {
  switch (slug) {
    case "for-the-first-impression":
      return {
        eyebrow: "Category one",
        title: "The opening gesture.",
        description:
          "Sharper silhouettes and a more declarative finish, made to land first and hold attention gently.",
      };
    case "for-the-long-haul":
      return {
        eyebrow: "Category two",
        title: "Made to stay close.",
        description:
          "Lighter construction, more flexibility, and the kind of shape that keeps its calm all day.",
      };
    case "for-elevated-moments":
      return {
        eyebrow: "Category three",
        title: "Quiet after dark.",
        description:
          "Quieter lines, warmer gold, and a more polished finish for evenings and sharper edits.",
      };
    default:
      return {
        eyebrow: "Collection",
        title: label,
        description: "A clean category page with one clear point of view.",
      };
  }
}

const fallbackProducts: Product[] = [
  {
    name: "Signature Edit",
    tag: "For the first impression",
    price: 240,
    summary: "A composed hero piece with a quiet profile, polished edges, and an immediate sense of intent.",
    details: ["Soft-touch surface", "Gold-toned hardware", "Built for daily wear"],
    handle: "signature-edit-1",
  },
  {
    name: "Everyday Line",
    tag: "For the long haul",
    price: 180,
    summary: "An easy essential that stays lightweight, keeps its shape, and settles into the day without noise.",
    details: ["Slim silhouette", "Structured interior", "Easy to carry"],
    handle: "everyday-line-1",
  },
  {
    name: "Evening Detail",
    tag: "For elevated moments",
    price: 320,
    summary: "A polished finish that moves from day to night while keeping the line calm and deliberate.",
    details: ["Minimal closure", "Warm gold accent", "Private-drop availability"],
    handle: "evening-detail-1",
  },
];

const brandStory = [
  {
    title: "One object, one mood",
    body: "Each page gives the product room to lead.",
  },
  {
    title: "Black does the framing",
    body: "White keeps the path clean while gold only marks the moments that matter.",
  },
  {
    title: "Built for live commerce",
    body: "Shopify keeps the catalog current and Dalenor keeps the presentation custom.",
  },
];

const serviceNotes = [
  {
    title: "Shipping",
    body: "Clear dispatch timing, shown early, so the buyer never has to guess.",
  },
  {
    title: "Packaging",
    body: "Soft black, white space, and one restrained gold note at the end.",
  },
  {
    title: "Support",
    body: "One place for product answers, care, and order help.",
  },
];
@customElement("moltbot-app")
export class MoltbotApp extends LitElement {
  @state() private catalogProducts: Product[] = fallbackProducts;
  @state() private catalogStatus: CatalogStatus = "loading";
  @state() private catalogShopName = "Shopify";
  @state() private catalogCurrency = "USD";
  @state() private catalogNote = "Loading Shopify catalog…";
  @state() private bagHandles: string[] = [];
  @state() private bagOpen = false;

  createRenderRoot() {
    return this;
  }

  protected firstUpdated() {
    this.loadBag();
    void this.loadCatalog();
  }

  private async loadCatalog() {
    try {
      const isLocalPreview =
        typeof window !== "undefined" &&
        (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
      const catalogUrl = isLocalPreview ? "/shopify/catalog.json" : "/api/shopify?limit=12";

      const response = await fetch(catalogUrl, {
        headers: {
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Shopify bridge returned ${response.status}`);
      }

      const data = (await response.json()) as ShopifyCatalogResponse;
      const products = data.products.map(normalizeProduct);

      if (products.length > 0) {
        this.catalogProducts = products;
        this.catalogStatus = "live";
        this.catalogShopName = data.shop.name || "Shopify";
        this.catalogCurrency = data.shop.currency || "USD";
        this.catalogNote = `Synced from ${data.shop.name || "your Shopify store"}`;
        return;
      }

      this.catalogStatus = "empty";
      this.catalogShopName = data.shop.name || "Shopify";
      this.catalogCurrency = data.shop.currency || "USD";
      this.catalogNote = "Shopify is connected, but the store has no products yet.";
    } catch (error) {
      console.warn("Shopify catalog fetch failed; using fallback products.", error);
      this.catalogStatus = "error";
      this.catalogShopName = "Shopify";
      this.catalogNote = "Using the premium fallback until the Shopify sync is available.";
    }
  }

  private loadBag() {
    if (typeof window === "undefined") return;

    try {
      const stored = window.localStorage.getItem("dalenor-bag");
      if (!stored) return;

      const parsed = JSON.parse(stored) as unknown;
      if (Array.isArray(parsed)) {
        this.bagHandles = parsed.filter((item): item is string => typeof item === "string");
      }
    } catch (error) {
      console.warn("Could not read bag state.", error);
    }
  }

  private persistBag() {
    if (typeof window === "undefined") return;

    try {
      window.localStorage.setItem("dalenor-bag", JSON.stringify(this.bagHandles));
    } catch (error) {
      console.warn("Could not persist bag state.", error);
    }
  }

  private toggleBag() {
    this.bagOpen = !this.bagOpen;
  }

  private closeBag() {
    this.bagOpen = false;
  }

  private addToBag(product: Product) {
    if (!product.handle) return;

    if (!this.bagHandles.includes(product.handle)) {
      this.bagHandles = [...this.bagHandles, product.handle];
      this.persistBag();
    }

    this.bagOpen = true;
  }

  private removeFromBag(handle: string) {
    this.bagHandles = this.bagHandles.filter((item) => item !== handle);
    this.persistBag();
  }

  private clearBag() {
    this.bagHandles = [];
    this.persistBag();
  }

  private currentRoute(): RouteState {
    if (typeof window === "undefined") {
      return { view: "home", categorySlug: null, productHandle: null };
    }

    const params = new URLSearchParams(window.location.search);
    const categorySlug = params.get("category");
    const productHandle = params.get("product");
    const rawView = params.get("view");
    const view = (rawView as ViewName | null) ?? (productHandle ? "product" : categorySlug ? "category" : "home");

    if (!["home", "shop", "collections", "category", "product", "story", "care"].includes(view)) {
      return { view: "home", categorySlug: null, productHandle: null };
    }

    return {
      view,
      categorySlug,
      productHandle,
    };
  }

  private href(params: Record<string, string | undefined>): string {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value) search.set(key, value);
    }
    const query = search.toString();
    return query ? `?${query}` : "/";
  }

  private productHref(product: Product): string {
    return this.href({ view: "product", product: product.handle });
  }

  private categoryHref(category: StoreCategory): string {
    return this.href({ view: "category", category: category.slug });
  }

  private shopHref(): string {
    return this.href({ view: "shop" });
  }

  private collectionsHref(): string {
    return this.href({ view: "collections" });
  }

  private storyHref(): string {
    return this.href({ view: "story" });
  }

  private careHref(): string {
    return this.href({ view: "care" });
  }

  private homeHref(): string {
    return "/";
  }

  private getProducts(): Product[] {
    return this.catalogProducts.length > 0 ? this.catalogProducts : fallbackProducts;
  }

  private getBagProducts(products: Product[]) {
    const bag = new Map(this.bagHandles.map((handle) => [handle, true]));
    return products.filter((product) => product.handle && bag.has(product.handle));
  }

  private getBagSubtotal(products: Product[]) {
    return this.getBagProducts(products).reduce((sum, product) => sum + product.price, 0);
  }

  private getCategories(products: Product[]): StoreCategory[] {
    const map = new Map<string, StoreCategory>();

    for (const product of products) {
      const slug = slugify(product.tag);
      const existing = map.get(slug);
      if (existing) {
        existing.productCount += 1;
        continue;
      }

      const copy = categoryCopy(slug, product.tag);
      map.set(slug, {
        slug,
        label: product.tag,
        description: copy.description,
        productCount: 1,
        featured: product,
      });
    }

    return Array.from(map.values());
  }

  private findProductByHandle(handle: string | null, products: Product[]): Product | null {
    if (!handle) return null;
    return products.find((product) => product.handle === handle) ?? null;
  }

  private findCategoryBySlug(slug: string | null, categories: StoreCategory[]): StoreCategory | null {
    if (!slug) return null;
    return categories.find((category) => category.slug === slug) ?? null;
  }

  private categoryProducts(categorySlug: string, products: Product[]): Product[] {
    return products.filter((product) => slugify(product.tag) === categorySlug);
  }

  private renderProductArt(product: Product, size: "hero" | "thumb" | "detail") {
    const className =
      size === "hero"
        ? "product-art product-art--hero"
        : size === "detail"
          ? "product-art product-art--detail"
          : "product-art product-art--thumb";

    if (product.image) {
      return html`
        <img
          class=${className}
          src=${product.image}
          alt=${product.name}
          width="1200"
          height="900"
          loading=${size === "hero" ? "eager" : "lazy"}
          decoding="async"
          fetchpriority=${size === "hero" ? "high" : "auto"}
        />
      `;
    }

    return html`
      <img
        class=${className}
        src=${storyCoverAsset}
        alt=${`${product.name} cover art`}
        width="7401"
        height="2784"
        loading=${size === "hero" ? "eager" : "lazy"}
        decoding="async"
        fetchpriority=${size === "hero" ? "high" : "auto"}
      />
    `;
  }

  private renderHeader(route: RouteState) {
    const activeShop = route.view === "shop" || route.view === "product";
    const activeCollections = route.view === "collections" || route.view === "category";

    return html`
      <header class="site-header">
        <a class="brand" href=${this.homeHref()} aria-label="Dalenor home">
          <img
            src="${assetBase}dalenor-logo-header-inverse.png"
            alt="Dalenor"
            width="805"
            height="326"
            loading="eager"
            decoding="async"
            fetchpriority="high"
          />
        </a>

        <nav class="site-nav" aria-label="Primary">
          <a href=${this.homeHref()} aria-current=${route.view === "home" ? "page" : ""}>Home</a>
          <a href=${this.shopHref()} aria-current=${activeShop ? "page" : ""}>Shop</a>
          <a href=${this.collectionsHref()} aria-current=${activeCollections ? "page" : ""}>Collections</a>
        </nav>

        <div class="header-actions">
          <a class="header-cta" href=${this.shopHref()}>Shop Now</a>
          <button
            class="header-bag"
            type="button"
            aria-expanded=${this.bagOpen ? "true" : "false"}
            @click=${() => this.toggleBag()}
          >
            Bag <span>${this.bagHandles.length}</span>
          </button>
        </div>
      </header>
    `;
  }
  private renderCategoryCard(category: StoreCategory) {
    const copy = categoryCopy(category.slug, category.label);
    return html`
      <a class="category-card" href=${this.categoryHref(category)}>
        <span class="category-card__eyebrow">${copy.eyebrow}</span>
        <h3>${category.label}</h3>
        <p>${category.description}</p>
        <div class="category-card__meta">
          <span>${category.productCount} piece${category.productCount === 1 ? "" : "s"}</span>
          <span>View category</span>
        </div>
      </a>
    `;
  }

  private renderProductCard(product: Product, size: "large" | "compact" = "compact") {
    const cardClass = size === "large" ? "product-card product-card--large" : "product-card";
    const detailLimit = 2;
    const summaryLength = size === "large" ? 92 : 82;
    const summary = excerpt(product.summary, summaryLength);
    const mediaLabel =
      size === "compact"
        ? html`
            <div class="product-card__media-copy" aria-hidden="true">
              <span>Dalenor</span>
              <strong>${product.name}</strong>
            </div>
          `
        : null;

    return html`
      <article class=${cardClass}>
        <a class="product-card__media" href=${this.productHref(product)} aria-label=${`Open ${product.name}`}>
          <div class="product-card__artframe">
            ${this.renderProductArt(product, size === "large" ? "detail" : "thumb")}
            ${mediaLabel}
          </div>
        </a>

        <div class="product-card__content">
          <div class="product-card__topline">
            <span class="product-card__category">${product.tag}</span>
            <span class="product-card__price">${formatPrice(product.price, this.catalogCurrency)}</span>
          </div>
          <h3 class="product-card__title"><a href=${this.productHref(product)}>${product.name}</a></h3>
          <p class="product-card__summary">${summary}</p>
          <ul class="product-card__details">
            ${product.details.slice(0, detailLimit).map((detail) => html`<li>${detail}</li>`)}
          </ul>
          <div class="product-card__actions">
            <a class="button button--gold" href=${this.productHref(product)}>View Details</a>
            <button class="button button--ghost button--small button--subtle" type="button" @click=${() => this.addToBag(product)}>Add to bag</button>
          </div>
        </div>
      </article>
    `;
  }

  private renderHomeView(products: Product[], categories: StoreCategory[]) {
    const statusLabel = this.catalogStatus === "live" ? "Shopify-synced catalog" : "Premium fallback";
    const featuredProduct = products[0] ?? fallbackProducts[0];

    return html`
      <section class="hero hero--feature">
        <div class="hero-copy">
          <p class="eyebrow">
            Dalenor storefront
            <span class="status-pill">${statusLabel}</span>
          </p>
          <h1>Premium, without the noise.</h1>
          <p class="lede">
            A calm black, white, and gold storefront with one clear path to browse and buy.
          </p>

          <div class="hero-actions">
            <a class="button button--gold" href=${this.shopHref()}>Shop All Products</a>
            <a class="button button--ghost" href=${this.collectionsHref()}>Browse Collections</a>
          </div>
        </div>

        <div class="hero-visual">
          <div class="hero-visual__frame hero-visual__frame--featured">
            <a class="hero-visual__art hero-visual__art--cover" href=${this.productHref(featuredProduct)} aria-label=${`Open ${featuredProduct.name}`}>
              ${this.renderProductArt(featuredProduct, "hero")}
            </a>

            <div class="hero-visual__caption">
              <span class="panel-label">Featured piece</span>
              <strong>${featuredProduct.name}</strong>
              <p>${excerpt(featuredProduct.summary, 84)}</p>
              <p class="hero-visual__meta">${featuredProduct.tag} · ${formatPrice(featuredProduct.price, this.catalogCurrency)}</p>
            </div>
          </div>
        </div>
      </section>

      <section class="section section--story">
        <div class="section-head section-head--compact">
          <p class="eyebrow">About Dalenor</p>
          <h2>A quieter store, by design.</h2>
          <p>One product lead, a tight palette, and a clear path from browsing to buying.</p>
        </div>

        <div class="story-lineup">
          ${brandStory.map(
            (item, index) => html`
              <article class="story-lineup__item">
                <span class="story-lineup__index">${String(index + 1).padStart(2, "0")}</span>
                <div class="story-lineup__copy">
                  <h3>${item.title}</h3>
                  <p>${item.body}</p>
                </div>
              </article>
            `,
          )}
        </div>
      </section>

      <section class="section section--categories">
        <div class="section-head">
          <p class="eyebrow">Collections</p>
          <h2>Three chapters. One calm store.</h2>
          <p>
            First impression, everyday, and evening detail each get their own page.
          </p>
        </div>

        <div class="category-grid">
          ${categories.map((category) => this.renderCategoryCard(category))}
        </div>
      </section>

      <section class="section section--collection">
        <div class="section-head">
          <p class="eyebrow">Shop preview</p>
          <h2>Selected pieces.</h2>
          <p>Live Shopify data, laid out with less noise and one clear path into each product.</p>
        </div>

        <div class="home-product-grid">
          ${products.map((product) => this.renderProductCard(product))}
        </div>
      </section>
    `;
  }

  private renderShopView(products: Product[], categories: StoreCategory[]) {
    return html`
      <section class="page-hero">
        <div class="page-hero__copy">
          <p class="eyebrow">
            Shop
            <span class="status-pill">${this.catalogStatus === "live" ? "Shopify-synced" : "Preview"}</span>
          </p>
          <h1>A calmer way to browse the catalog.</h1>
          <p>Categories on one side, products on the other, and one direct path into each piece.</p>
        </div>

        <div class="page-hero__meta">
          <div>
            <span class="page-hero__label">Store</span>
            <strong>${this.catalogShopName}</strong>
          </div>
          <div>
            <span class="page-hero__label">Catalog</span>
            <strong>${products.length} products</strong>
          </div>
          <div>
            <span class="page-hero__label">Currency</span>
            <strong>${this.catalogCurrency}</strong>
          </div>
        </div>
      </section>

      <section class="shop-layout">
        <aside class="shop-aside">
          <div class="shop-aside__panel">
            <p class="eyebrow">Browse by category</p>
            <div class="chip-list">
              <a class="chip chip--active" href=${this.shopHref()}>All</a>
              ${categories.map(
                (category) => html`<a class="chip" href=${this.categoryHref(category)}>${category.label}</a>`,
              )}
            </div>
          </div>

          <div class="shop-aside__panel">
            <p class="eyebrow">Store notes</p>
            <div class="info-list">
              ${serviceNotes.map(
                (note) => html`
                  <article class="info-card info-card--tight">
                    <h3>${note.title}</h3>
                    <p>${note.body}</p>
                  </article>
                `,
              )}
            </div>
          </div>
        </aside>

        <div class="shop-grid">
          ${products.map((product) => this.renderProductCard(product, "large"))}
        </div>
      </section>
    `;
  }

  private renderCollectionsView(categories: StoreCategory[]) {
    return html`
      <section class="page-hero">
        <div class="page-hero__copy">
          <p class="eyebrow">Collections</p>
          <h1>Three chapters. One calm rhythm.</h1>
          <p>First impression, everyday, and evening detail each get their own page.</p>
        </div>

        <div class="page-hero__meta">
          <div>
            <span class="page-hero__label">Collection depth</span>
            <strong>${categories.length} categories</strong>
          </div>
          <div>
            <span class="page-hero__label">Style system</span>
            <strong>Black / White / Gold</strong>
          </div>
          <div>
            <span class="page-hero__label">Backend</span>
            <strong>Shopify synced</strong>
          </div>
        </div>
      </section>

      <section class="category-grid category-grid--large">
        ${categories.map((category) => this.renderCategoryCard(category))}
      </section>
    `;
  }
  private renderCategoryView(category: StoreCategory, products: Product[], categories: StoreCategory[]) {
    const copy = categoryCopy(category.slug, category.label);
    const categoryProducts = this.categoryProducts(category.slug, products);

    return html`
      <section class="page-hero page-hero--category">
        <div class="page-hero__copy">
          <p class="eyebrow">${copy.eyebrow}</p>
          <h1>${copy.title}</h1>
          <p>${copy.description}</p>
        </div>

        <div class="page-hero__meta">
          <div>
            <span class="page-hero__label">Category</span>
            <strong>${category.label}</strong>
          </div>
          <div>
            <span class="page-hero__label">Products</span>
            <strong>${category.productCount}</strong>
          </div>
          <div>
            <span class="page-hero__label">Next step</span>
            <strong>Open the product page</strong>
          </div>
        </div>
      </section>

      <section class="shop-layout">
        <aside class="shop-aside">
          <div class="shop-aside__panel">
            <p class="eyebrow">Other categories</p>
            <div class="chip-list">
              ${categories.map(
                (item) => html`<a class=${`chip ${item.slug === category.slug ? "chip--active" : ""}`} href=${this.categoryHref(item)}>${item.label}</a>`,
              )}
            </div>
          </div>

          <div class="shop-aside__panel">
            <p class="eyebrow">Category note</p>
            <p>${copy.description}</p>
          </div>
        </aside>

        <div class="shop-grid">
          ${categoryProducts.map((product) => this.renderProductCard(product, "large"))}
        </div>
      </section>
    `;
  }

  private renderProductView(product: Product, products: Product[], categories: StoreCategory[]) {
    const category = categories.find((item) => item.slug === slugify(product.tag)) ?? categories[0];
    const relatedProducts = products.filter((item) => item.handle !== product.handle).slice(0, 3);

    return html`
      <section class="product-layout">
        <div class="product-layout__gallery">
          <p class="eyebrow">${product.tag}</p>
          <a class="product-layout__art" href=${this.categoryHref(category ?? categories[0])} aria-label=${`Open ${product.tag} category`}>
            ${this.renderProductArt(product, "hero")}
          </a>

          <div class="product-layout__mini">
            <div>
              <span class="page-hero__label">Price</span>
              <strong>${formatPrice(product.price, this.catalogCurrency)}</strong>
            </div>
            <div>
              <span class="page-hero__label">Status</span>
              <strong>Shopify synced</strong>
            </div>
            <div>
              <span class="page-hero__label">Category</span>
              <strong>${product.tag}</strong>
            </div>
          </div>
        </div>

        <aside class="product-layout__panel">
          <p class="eyebrow">Product detail</p>
          <h1>${product.name}</h1>
          <p class="lede">${product.summary}</p>
          <p class="product-price">${formatPrice(product.price, this.catalogCurrency)}</p>

          <div class="detail-points">
            ${product.details.map((detail) => html`<div class="detail-point">${detail}</div>`)}
          </div>

          <div class="hero-actions">
            <a class="button button--gold" href=${this.categoryHref(category ?? categories[0])}>See category</a>
            <a class="button button--ghost" href=${this.shopHref()}>Back to shop</a>
          </div>
        </aside>
      </section>

      <section class="section">
        <div class="section-head section-head--compact">
          <p class="eyebrow">Related</p>
          <h2>Complete the set.</h2>
          <p>Other pieces in the range, arranged so the store feels connected rather than isolated.</p>
        </div>

        <div class="home-product-grid">
          ${relatedProducts.map((item) => this.renderProductCard(item))}
        </div>
      </section>
    `;
  }

  private renderStoryView(products: Product[]) {
    const storyProducts = products.length > 0 ? products : fallbackProducts;
    const featured = storyProducts[0];
    const chapterProducts = storyProducts.slice(0, 3);

    return html`
      <section class="page-hero">
        <div class="page-hero__copy">
          <p class="eyebrow">Story</p>
          <h1>The house behind the store.</h1>
          <p>
            This page gives the brand room to speak without cluttering the shopping experience.
          </p>
        </div>

        <div class="page-hero__meta">
          <div>
            <span class="page-hero__label">Tone</span>
            <strong>Calm & premium</strong>
          </div>
          <div>
            <span class="page-hero__label">Palette</span>
            <strong>Black, white & gold</strong>
          </div>
          <div>
            <span class="page-hero__label">Catalog</span>
            <strong>${products.length} pieces</strong>
          </div>
        </div>
      </section>

      <section class="story-feature">
        <div class="story-feature__media">
          <p class="eyebrow">Scroll story</p>
          <a class="story-feature__art" href=${this.productHref(featured)} aria-label=${`Open ${featured.name}`}>
            <img
              class="product-art product-art--hero"
              src="${featured.image ?? storyCoverAsset}"
              alt=${`${featured.name} cover art`}
              width="7401"
              height="2784"
              loading="eager"
              decoding="async"
              fetchpriority="high"
            />
          </a>

          <div class="story-feature__note">
            <span class="page-hero__label">Cover image</span>
            <strong>${featured.name}</strong>
            <p>
              A single visual anchor keeps the page calm while you scroll through the brand story.
            </p>
          </div>
        </div>

        <div class="story-feature__copy">
          <article class="story-step story-step--lead">
            <span class="story-step__index">01</span>
            <div class="story-step__body">
              <p class="eyebrow">First look</p>
              <h2>Built to feel premium without trying too hard.</h2>
              <p>
                The product stays visible while the copy moves, so the page reads like a lookbook instead of a wall of text.
              </p>
            </div>
          </article>

          ${chapterProducts.map(
            (product, index) => html`
              <article class="story-step">
                <span class="story-step__index">${String(index + 2).padStart(2, "0")}</span>
                <div class="story-step__body">
                  <h3>${product.name}</h3>
                  <p>${product.summary}</p>
                </div>
                <a class="story-step__thumb" href=${this.productHref(product)} aria-label=${`Open ${product.name}`}>
                  ${this.renderProductArt(product, "thumb")}
                </a>
              </article>
            `,
          )}
        </div>
      </section>

      <section class="info-grid">
        ${brandStory.map(
          (principle) => html`
            <article class="info-card">
              <h3>${principle.title}</h3>
              <p>${principle.body}</p>
            </article>
          `,
        )}
      </section>
    `;
  }

  private renderCareView() {
    return html`
      <section class="page-hero">
        <div class="page-hero__copy">
          <p class="eyebrow">Care</p>
          <h1>Shipping, packaging, and support.</h1>
          <p>
            A real storefront needs a clear support page. This one keeps the answer to the common questions in one place.
          </p>
        </div>

        <div class="page-hero__meta">
          <div>
            <span class="page-hero__label">Returns</span>
            <strong>Simple & direct</strong>
          </div>
          <div>
            <span class="page-hero__label">Packaging</span>
            <strong>Premium presentation</strong>
          </div>
          <div>
            <span class="page-hero__label">Contact</span>
            <strong>Shopify-managed support</strong>
          </div>
        </div>
      </section>

      <section class="info-grid">
        ${serviceNotes.map(
          (note) => html`
            <article class="info-card">
              <h3>${note.title}</h3>
              <p>${note.body}</p>
            </article>
          `,
        )}
      </section>

      <section class="section">
        <div class="cta-panel">
          <div class="cta-panel__copy">
            <p class="eyebrow">Need next step</p>
            <h2>Back to the store.</h2>
            <p>Use the shop page for the catalog, the collections page for category browsing, and the product page for detail.</p>
          </div>

          <div class="cta-panel__actions">
            <a class="button button--gold" href=${this.shopHref()}>Shop all</a>
            <a class="button button--ghost" href=${this.collectionsHref()}>View collections</a>
          </div>
        </div>
      </section>
    `;
  }

  render() {
    const products = this.getProducts();
    const categories = this.getCategories(products);
    const route = this.currentRoute();
    const selectedCategory = this.findCategoryBySlug(route.categorySlug, categories) ?? categories[0] ?? null;
    const selectedProduct = this.findProductByHandle(route.productHandle, products) ?? products[0] ?? null;

    let view = html``;
    switch (route.view) {
      case "shop":
        view = this.renderShopView(products, categories);
        break;
      case "collections":
        view = this.renderCollectionsView(categories);
        break;
      case "category":
        view = this.renderCategoryView(selectedCategory ?? categories[0], products, categories);
        break;
      case "product":
        view = this.renderProductView(selectedProduct ?? products[0], products, categories);
        break;
      case "story":
        view = this.renderStoryView(products);
        break;
      case "care":
        view = this.renderCareView();
        break;
      default:
        view = this.renderHomeView(products, categories);
        break;
    }

    return html`
      <div class="page-shell" id="top">
        <a class="skip-link" href="#main-content">Skip to content</a>
        ${this.renderHeader(route)}

        <main id="main-content">
          ${view}
        </main>

        ${this.renderBagDrawer(products)}

        <footer class="site-footer">
        <div class="site-footer__brand">
          <span class="site-footer__label">Dalenor</span>
          <p>${this.catalogStatus === "live" ? `Connected to ${this.catalogShopName}` : "Black, white, and gold only."}</p>
          <p class="site-footer__note">Private drops, clear product pages, and a store designed to stay calm.</p>
        </div>

          <div class="site-footer__links">
            <a href=${this.shopHref()}>Shop</a>
            <a href=${this.collectionsHref()}>Collections</a>
            <a href=${this.storyHref()}>Story</a>
            <a href=${this.careHref()}>Care</a>
          </div>

          <div class="site-footer__contact">
            <span class="site-footer__label">Contact</span>
            <a href="mailto:hello@dalenor.com">hello@dalenor.com</a>
            <a href="mailto:hello@dalenor.com?subject=Join%20the%20Dalenor%20list">Join the list</a>
          </div>
        </footer>
      </div>
    `;
  }

  private renderBagDrawer(products: Product[]) {
    const bagProducts = this.getBagProducts(products);
    const subtotal = this.getBagSubtotal(products);

    return html`
      <div
        class=${`bag-backdrop ${this.bagOpen ? "bag-backdrop--open" : ""}`}
        role="presentation"
        @click=${() => this.closeBag()}
      ></div>

      <aside class=${`bag-drawer ${this.bagOpen ? "bag-drawer--open" : ""}`} aria-label="Shopping bag" aria-hidden=${this.bagOpen ? "false" : "true"}>
        <div class="bag-drawer__header">
          <div>
            <p class="eyebrow">Bag</p>
            <h2>Your shortlist</h2>
          </div>

          <button class="bag-drawer__close" type="button" @click=${() => this.closeBag()}>Close</button>
        </div>

        <p class="bag-drawer__note">
          Saved locally in this browser. Use it to compare pieces without breaking the clean shopping flow.
        </p>

        <div class="bag-drawer__items">
          ${bagProducts.length > 0
            ? bagProducts.map(
                (product) => html`
                  <article class="bag-item">
                    <a class="bag-item__art" href=${this.productHref(product)} aria-label=${`Open ${product.name}`}>
                      ${this.renderProductArt(product, "thumb")}
                    </a>

                    <div class="bag-item__copy">
                      <div class="bag-item__topline">
                        <strong>${product.name}</strong>
                        <button type="button" class="bag-item__remove" @click=${() => this.removeFromBag(product.handle ?? "")}>Remove</button>
                      </div>
                      <span>${product.tag}</span>
                      <span>${formatPrice(product.price, this.catalogCurrency)}</span>
                    </div>
                  </article>
                `,
              )
            : html`<div class="bag-empty">Your bag is empty. Add one piece to keep browsing focused.</div>`}
        </div>

        <div class="bag-drawer__footer">
          <div class="bag-total">
            <span class="page-hero__label">Subtotal</span>
            <strong>${formatPrice(subtotal, this.catalogCurrency)}</strong>
          </div>

          <div class="hero-actions hero-actions--compact">
            <a class="button button--gold" href=${this.shopHref()}>Continue shopping</a>
            <button class="button button--ghost button--small" type="button" @click=${() => this.clearBag()}>Clear bag</button>
          </div>
        </div>
      </aside>
    `;
  }
}
